/**
 * The Hub's icons. One set, one weight, one size scale, chosen once.
 *
 * ── Why this file exists at all ────────────────────────────────────────────
 *
 * Kyu, on what he wants the Hub to feel like: "when you go to an iPhone or a
 * Mac, you go to Settings... some very recognizable icons. Very easy on the
 * eye." Before this file the Hub had NO icons — no icon package in
 * package.json, and exactly one inline <svg> in the whole codebase. An iOS
 * Settings row is icon, label, value, chevron; take the icon away and the
 * pattern collapses back into a table, which is what every settings-shaped
 * screen here already looked like.
 *
 * He picked Lucide on 2 September, from three sets rendered on his real
 * sidebar and a real settings list. That decision is spent — it is not
 * re-opened per screen, and a second set is never introduced beside this one.
 *
 * ── Why the paths are vendored rather than `npm install lucide-react` ──────
 *
 * Not a judgement about the package, which is the better long-term answer. It
 * is about a collision that was live at the time: the other dev thread had an
 * unpushed package.json sitting in the same working folder. A new dependency
 * means both package.json and the lock file conflict, and a lock-file merge is
 * the expensive kind — the shape of problem that cost this project four hours
 * on 1 September.
 *
 * The Hub needs about twenty icons. Twenty paths cost nothing, add no
 * dependency, and cannot conflict with anybody. When the Hub starts needing
 * icons faster than someone can paste one in, switching to the package is a
 * change to THIS FILE ONLY, because nothing anywhere imports an icon directly.
 * That is the whole reason the indirection exists.
 *
 * Paths are Lucide v1.39.0, ISC licensed, copied verbatim. Do not redraw them
 * by hand: an icon nudged to look better at one size is how a set stops
 * looking like a set.
 *
 * ── The rules this component enforces by not offering a way round them ─────
 *
 * NO COLOUR PROP. Every icon paints in `currentColor`, so it takes the colour
 * of the text it sits beside and follows all four themes for free. A colour
 * prop would mean an icon could disagree with its own label, and the first one
 * that does gets copied.
 *
 * NO FREE SIZE. Three sizes, named. A number prop produces 15px here and 17px
 * there and a grid that drifts — the same failure the fixed-height deal card
 * was built to stop, one layer down.
 *
 * NO STROKE PROP. 2px, always, which is Lucide's own weight at its own 24 grid.
 * Mixed weights are the single most visible way a set stops reading as one.
 */

export const ICON_SIZES = {
  /** Beside dense metadata — table cells, badges, foot rows. */
  sm: 14,
  /** The default. Beside body text: nav labels, buttons, inline actions. */
  md: 16,
  /** The leading icon of a grouped row, where it anchors the row's height. */
  lg: 18,
} as const;

export type IconSize = keyof typeof ICON_SIZES;

/**
 * The set. Keys are the Hub's word for the thing, not Lucide's — `deals`
 * rather than `handshake` — so a later swap of set or of glyph happens here
 * and every call site keeps reading correctly.
 */
const PATHS = {
  home: '<path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8" /><path d="M3 10a2 2 0 0 1 .709-1.528l7-6a2 2 0 0 1 2.582 0l7 6A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />',
  tasks: '<rect width="18" height="18" x="3" y="3" rx="2" /><path d="m16 9-5.5 5.5L8 12" />',
  notes:
    '<path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z" />',
  inbox:
    '<polyline points="22 12 16 12 14 15 10 15 8 12 2 12" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />',
  projects:
    '<path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />',
  social:
    '<path d="M11 6a13 13 0 0 0 8.4-2.8A1 1 0 0 1 21 4v12a1 1 0 0 1-1.6.8A13 13 0 0 0 11 14H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z" /><path d="M6 14a12 12 0 0 0 2.4 7.2 2 2 0 0 0 3.2-2.4A8 8 0 0 1 10 14" /><path d="M8 6v8" />',
  nurture:
    '<path d="M14 9.536V7a4 4 0 0 1 4-4h1.5a.5.5 0 0 1 .5.5V5a4 4 0 0 1-4 4 4 4 0 0 0-4 4c0 2 1 3 1 5a5 5 0 0 1-1 3" /><path d="M4 9a5 5 0 0 1 8 4 5 5 0 0 1-8-4" /><path d="M5 21h14" />',
  tracker: '<path d="M3 3v16a2 2 0 0 0 2 2h16" /><path d="m19 9-5 5-4-4-3 3" />',
  deals:
    '<path d="m11 17 2 2a1 1 0 1 0 3-3" /><path d="m14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4" /><path d="m21 3 1 11h-2" /><path d="M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3" /><path d="M3 4h8" />',
  orders:
    '<path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z" /><path d="M12 22V12" /><polyline points="3.29 7 12 12 20.71 7" /><path d="m7.5 4.27 9 5.15" />',
  brief:
    '<path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5z" /><polyline points="14 2 14 8 20 8" /><line x1="16" x2="8" y1="13" y2="13" /><line x1="16" x2="8" y1="17" y2="17" /><line x1="10" x2="8" y1="9" y2="9" />',
  reviews:
    '<path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" />',
  files:
    '<path d="M15 2h-4a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V8" /><path d="M16.706 2.706A2.4 2.4 0 0 0 15 2v5a1 1 0 0 0 1 1h5a2.4 2.4 0 0 0-.706-1.706z" /><path d="M5 7a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h8a2 2 0 0 0 1.732-1" />',
  admin:
    '<path d="M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 0 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915" /><circle cx="12" cy="12" r="3" />',
  profile: '<path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />',
  bell: '<path d="M10.268 21a2 2 0 0 0 3.464 0" /><path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" />',
  workspace:
    '<path d="M10 12h4" /><path d="M10 8h4" /><path d="M14 21v-3a2 2 0 0 0-4 0v3" /><path d="M6 10H4a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-2" /><path d="M6 21V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16" />',
  billing:
    '<rect width="20" height="14" x="2" y="5" rx="2" /><line x1="2" x2="22" y1="10" y2="10" />',
  theme:
    '<path d="M12 22a1 1 0 0 1 0-20 10 9 0 0 1 10 9 5 5 0 0 1-5 5h-2.25a1.75 1.75 0 0 0-1.4 2.8l.3.4a1.75 1.75 0 0 1-1.4 2.8z" /><circle cx="13.5" cy="6.5" r=".5" fill="currentColor" /><circle cx="17.5" cy="10.5" r=".5" fill="currentColor" /><circle cx="6.5" cy="12.5" r=".5" fill="currentColor" /><circle cx="8.5" cy="7.5" r=".5" fill="currentColor" />',
  textSize:
    '<path d="M12 4v16" /><path d="M4 7V5a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v2" /><path d="M9 20h6" />',
  clock: '<circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />',
  // My Day: the one page that is about a day rather than a thing.
  sun: '<circle cx="12" cy="12" r="4" /><path d="M12 2v2" /><path d="M12 20v2" /><path d="m4.93 4.93 1.41 1.41" /><path d="m17.66 17.66 1.41 1.41" /><path d="M2 12h2" /><path d="M20 12h2" /><path d="m6.34 17.66-1.41 1.41" /><path d="m19.07 4.93-1.41 1.41" />',
  calendar:
    '<path d="M8 2v3" /><path d="M16 2v3" /><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18" />',
  flag: '<path d="M4 22V4a1 1 0 0 1 .4-.8A6 6 0 0 1 8 2c3 0 5 2 7.333 2q2 0 3.067-.8A1 1 0 0 1 20 4v10a1 1 0 0 1-.4.8A6 6 0 0 1 16 16c-3 0-5-2-8-2a6 6 0 0 0-4 1.528" />',
  comment:
    '<path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719" />',
  search: '<path d="m21 21-4.34-4.34" /><circle cx="11" cy="11" r="8" />',
  filter:
    '<path d="M10 20a1 1 0 0 0 .553.895l2 1A1 1 0 0 0 14 21v-7a2 2 0 0 1 .517-1.341L21.74 4.67A1 1 0 0 0 21 3H3a1 1 0 0 0-.742 1.67l7.225 7.989A2 2 0 0 1 10 14z" />',
  link: '<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />',
  add: '<path d="M5 12h14" /><path d="M12 5v14" />',
  close: '<path d="M18 6 6 18" /><path d="m6 6 12 12" />',
  chevron: '<path d="m9 18 6-6-6-6" />',
  delete:
    '<path d="M10 11v6" /><path d="M14 11v6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />',
  signOut:
    '<path d="m16 17 5-5-5-5" /><path d="M21 12H9" /><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />',
} as const;

export type IconName = keyof typeof PATHS;

export const ICON_TONES = ['blue', 'orange', 'amber', 'purple', 'pink', 'green', 'grey'] as const;
export type IconTone = (typeof ICON_TONES)[number];

export const ICON_NAMES = Object.keys(PATHS) as IconName[];

export interface IconProps {
  name: IconName;
  /** Defaults to `md`. Three values, and there is no fourth. */
  size?: IconSize;
  /**
   * Only for an icon that is the ONLY thing carrying the meaning — an icon-only
   * button. An icon beside its own label must NOT take one: a screen reader
   * would then read the label twice, which is worse than silence. Left off, the
   * icon is hidden from assistive tech, which is the honest default because
   * decorative is what it usually is.
   */
  label?: string | undefined;
  className?: string | undefined;
}

export function Icon({ name, size = 'md', label, className }: IconProps) {
  const px = ICON_SIZES[size];
  return (
    <svg
      className={className === undefined ? 'ui-icon' : `ui-icon ${className}`}
      width={px}
      height={px}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={label === undefined ? true : undefined}
      role={label === undefined ? undefined : 'img'}
      aria-label={label}
      // The paths are constants in this file — never a prop, never a fetch, and
      // never anything a user or a row of data can reach. That is the only
      // reason this is safe, and it is why `PATHS` is not exported.
      dangerouslySetInnerHTML={{ __html: PATHS[name] }}
    />
  );
}

/** A fixed-hue identity tile shared by the sidebar and profile card heads. */
export function IconTile({
  name,
  tone,
  className,
}: {
  name: IconName;
  tone: IconTone;
  className?: string | undefined;
}) {
  return (
    <span
      className={className === undefined ? 'ui-icon-tile' : `ui-icon-tile ${className}`}
      data-icon-name={name}
      data-icon-tone={tone}
      aria-hidden="true"
    >
      <Icon name={name} size="sm" />
    </span>
  );
}
